<template>
    <div class="uploadsolo">
        <div class="container">
            <div class="hero is-primary">
                <div class="hero-body" style="padding-top: 24px; padding-bottom: 24px;">
                    <div class="container">
                    <h1 class="title is-size-1">Solo Scorecards</h1>
                    <p class="subtitle">Upload your solo scorecard photo</p>
                    </div>
                </div>
            </div>
            <br/>
            <div class="ai-note">
                <p>
                    <i class="fas fa-robot"></i>&nbsp;
                    We use <strong>AI technology</strong> to read your scorecard. You will have a chance to review the uploaded data before it's posted.
                    <router-link to="/privacy" style="color: #3273dc; text-decoration: underline;">Learn more about our privacy policy</router-link>.
                </p>
            </div>
            <div class="template-note">
                <p>
                    <i class="fas fa-file-pdf"></i>&nbsp;
                    Don't have a scorecard? <a :href="`${publicPath}files/Axes2_Solo.pdf`" download style="color: #3273dc; text-decoration: underline;">Download our scorecard template</a>.
                </p>
            </div>
            <div v-if="!hasData">
                <p class="is-size-4 page-header-custom">Upload Solo Scorecard:</p><br/>
                <div class="uploadsoloform">
                    <form @submit.prevent="submitForm">
                        <div class="file has-name is-boxed is-centered is-primary is-fullwidth">
                            <label class="file-label">
                                <input 
                                    class="file-input" 
                                    type="file" 
                                    accept="image/*"
                                    capture="environment"
                                    @change="handleFileUpload"
                                    ref="fileInput"
                                />
                                <span class="file-cta">
                                    <span class="file-icon">
                                        <i class="fas fa-camera"></i>
                                    </span>
                                    <span class="file-label">
                                        Choose a file…
                                    </span>
                                </span>
                                <span class="file-name" v-if="fileName">
                                    {{ fileName }}
                                </span>
                                <span class="file-name" v-else>
                                    No file selected
                                </span>
                            </label>
                        </div>

                    </form>
                    <div v-show="pleaseWait" class="loading-overlay">
                        <img :src="`${publicPath}img/pleasewait.gif`" style="width: 50px;"/>
                    </div>
                </div>
                <div class="has-text-centered" style="margin-top: 20px;">
                    <button class="button is-danger is-fullwidth is-medium" @click="$router.push('/home')">
                        <span class="icon"><i class="fas fa-times"></i></span>
                        <span>Cancel</span>
                    </button>
                </div>
            </div>
            <div>
                <br/>
                <div v-for="round in this.soloData.rounds" :key="round.id">
                    <div v-for="match in round.matches" v-if="round.title && round.title.trim() !== ''">
                        <p class="roundTitle">{{ round.title }}</p>
                        <div v-for="player in match.matchPlayers">
                            <span v-for="thrw in player.throws">
                                <input type="text" class="basicScore" v-model="thrw.basicScore" @input="recalcTotals"/> 
                            </span>
                            <span class="total-wrapper">&nbsp;Total
                                <input type="text" class="totalScore" v-model="player.total"/>
                            </span>
                        </div>
                        <br/>
                    </div>
                </div>
                <br/>
                <div v-if="this.soloData.total != null">
                    <p class="finalTotal">Final Total: <input type="text" class="finalScore" v-model="this.soloData.total"/></p>
                    <br/>
                    
                    <div class="field mb-4">
                        <label class="label has-text-grey-light has-text-left">Date:</label>
                        <div class="control">
                            <input class="input date-input" type="date" v-model="scoreDate">
                        </div>
                    </div>
                    
                    <div class="field mb-4" v-if="$auth.user">
                        <label class="label has-text-grey-light has-text-left">Player:</label>
                        <p class="has-text-white has-text-weight-bold">{{ $auth.user.name || $auth.user.nickname }}</p>
                    </div>
                    <br/>
                    <form @submit.prevent="submitScores">
                        <div class="columns is-mobile">
                            <div class="column">
                                <button type="submit" class="button is-success is-fullwidth is-medium">
                                    <span class="icon"><i class="fas fa-check"></i></span>
                                    <span>Submit</span>
                                </button>
                            </div>
                            <div class="column">
                                <button type="button" class="button is-danger is-fullwidth is-medium" @click="$router.push('/home')">
                                    <span class="icon"><i class="fas fa-times"></i></span>
                                    <span>Cancel</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import emptygame from '../json/emptygame.json'
import { Axes2Match, Axes2MatchPlayer, Axes2Round, Axes2Throw } from '../models/Axes2Models';
import { handleRefresh, generateUniqueId } from '../utils/Axes2Utils';

export default {
  name: 'uploadsolo',
    beforeRouteLeave (to, from, next) {
        //navigation guard (back/forward navigation; refresh functionality attached to window below in plain JS)
        this.confirmLeaving(to, from, next);
    },
    components : {
    },
    filters: {
    },
    computed: {
        hasData() {
            return this.soloData && this.soloData.rounds && this.soloData.rounds.length > 0;
        }
    },
    data () {
        return {
            gameData: emptygame,
            soloData: {},
            pleaseWait: false,
            publicPath: process.env.BASE_URL,
            fileName: null,
            scoreDate: new Date().toISOString().substr(0, 10),
            debugMode: (process.env.VUE_APP_DEBUG_MODE === "true"),
            scorecardSubmitted: false,
        }
    },    
    methods:{
        getFormattedDate(dateString) {
             // Try to parse the date from the API response, otherwise default to today
             if (!dateString) return new Date().toISOString().substr(0, 10);
             try {
                 return new Date(dateString).toISOString().substr(0, 10);
             } catch (e) {
                 return new Date().toISOString().substr(0, 10);
             }
        },
        transformToSoloGameSchema() {
            if (!this.$auth.user) {
                throw new Error("User not authenticated. Cannot transform data.");
            }
            if (!this.soloData || !this.soloData.rounds) {
                 throw new Error("No scorecard data available to transform.");
            }

            // Create a clean player object without Auth0 custom namespace fields
            var player = new this.$playerClass(
                1, 
                this.$auth.user.nickname, 
                this.$auth.user.name, 
                this.$auth.user.given_name, 
                this.$auth.user.family_name, 
                this.$auth.user.email, 
                this.$auth.user.picture
            );

            return {
                "gameType": "Solo",
                "source": "upload",
                "id": Date.now(), 
                "title": "Uploaded Scorecard",
                "players": [player],
                "finalPlayers": null,
                "rounds": this.soloData.rounds,
                "dateTimeStamp": new Date(this.scoreDate).toISOString(),
                "subscriber": this.$auth.user.email,
                "location": null 
            };
        },
        recalcTotals() {
            if (this.soloData?.rounds) {
                let finalTotal = 0;
                
                this.soloData.rounds.forEach(round => {
                    let roundTotal = 0;
                    
                    round.matches.forEach(match => {
                        match.matchPlayers.forEach(matchPlayer => {
                            matchPlayer.throws.forEach(pThrow => {
                                const val = parseInt(pThrow.basicScore);
                                roundTotal += isNaN(val) ? 0 : val;
                            });
                            matchPlayer.total = roundTotal;
                        });
                    });
                    
                    finalTotal += roundTotal;
                });
                
                this.soloData.total = finalTotal;
            }
        },
        confirmLeaving(to, from, next) {
            // If scorecard has been submitted, allow navigation without confirmation
            if (this.scorecardSubmitted) {
                next();
                return;
            }
            if (confirm("Are you sure you want to leave?  Your scorecard has not been submitted.")) {
                next();
            } else {
                next(false);
            }
        },        
        handleFileUpload(event) {
            this.selectedFile = event.target.files[0];
            this.fileName = this.selectedFile ? this.selectedFile.name : null;
            // Auto-submit when file is selected
            if (this.selectedFile) {
                this.submitForm();
            }
        },
        parseLimitedInteger(value) {
            // Convert the input to a string and take the first character if it's longer than one character
            const char = String(value).length > 1 ? String(value)[0] : String(value);

            // Try to convert the character to an integer
            const num = parseInt(char, 10);

            // Check if the result is a valid integer between 0 and 5
            if (!isNaN(num) && num >= 0 && num <= 5) {
                return num;
            }

            // Return null if the value is not a valid integer in the range
            return null;
        },
        sumIntegers(...numbers) {
            return numbers.reduce((total, num) => {
                return total + (num != null ? num : 0);
            }, 0);
        },
        async submitScores() {
            var token = this.$parent.getApiToken();
            var url = (this.debugMode ? "http://localhost:8080/scorecard" : "https://axes2api.azurewebsites.net/scorecard");
            
            // Transform soloData to match schema
            var gamePayload = this.transformToSoloGameSchema();
            
            var jsonString = JSON.stringify(gamePayload);

            try {
                await axios(url, {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        'authorization': token
                    },
                    data: jsonString,
                });

                this.scorecardSubmitted = true;
                window.removeEventListener("beforeunload", handleRefresh, true);
                
                alert("Scorecard submitted successfully!");
                this.$router.push('/home');
            } catch (error) {
                console.error("Error submitting scorecard:", error);
                alert("Error submitting scorecard: " + error.message);
            }
        },
        async submitForm() {
            if (!this.selectedFile) {
                alert("Please select a file first.");
                return;
            }

            const formData = new FormData();
            formData.append("image", this.selectedFile);

            this.pleaseWait = true;

            let token = this.$parent.getApiToken();

            try {

                let url = process.env.VUE_APP_DOCINTEL_URL;
                if (this.debugMode) {
                    url = "http://localhost:3000/uploadsolo";
                }

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        'authorization': `Bearer ${token}`
                    },
                    body: formData,
                });
                
                if (!response.ok) {
                    throw new Error(`Upload failed with status: ${response.status}`);
                }

                const result = await response.json();
                
                const dataArray = result.dataArray;
                
                this.soloData = { "rounds": [] };

                if (result.date) {
                    this.scoreDate = this.getFormattedDate(result.date);
                } else {
                     this.scoreDate = this.getFormattedDate(null); // Default to today
                }

                var grandTotal = 0;

                for (var i = 0; i < dataArray.length; i++) {
                    var rndData = dataArray[i];

                    if (rndData.round_number != null && rndData.round_number != "") {
                        // Create a clean player object without Auth0 custom namespace fields
                        var plyr = new this.$playerClass(
                            1, 
                            this.$auth.user.nickname, 
                            this.$auth.user.name, 
                            this.$auth.user.given_name, 
                            this.$auth.user.family_name, 
                            this.$auth.user.email, 
                            this.$auth.user.picture
                        );

                        var thrws = [];

                        var throw1 = this.parseLimitedInteger(rndData.throw1);
                        var throw2 = this.parseLimitedInteger(rndData.throw2);
                        var throw3 = this.parseLimitedInteger(rndData.throw3);
                        var throw4 = this.parseLimitedInteger(rndData.throw4);
                        var throw5 = this.parseLimitedInteger(rndData.throw5);
                        var throw6 = this.parseLimitedInteger(rndData.throw6);

                        thrws.push(new Axes2Throw(1, throw1));
                        thrws.push(new Axes2Throw(2, throw2));
                        thrws.push(new Axes2Throw(3, throw3));
                        thrws.push(new Axes2Throw(4, throw4));
                        thrws.push(new Axes2Throw(5, throw5));
                        thrws.push(new Axes2Throw(6, throw6));

                        var sum = this.sumIntegers(throw1, throw2, throw3, throw4, throw5, throw6);
                        grandTotal += sum;

                        var matchPlayers = [];
                        var matchPlayer = new Axes2MatchPlayer(generateUniqueId(), thrws, plyr, false);
                        matchPlayer.total = sum;

                        matchPlayers.push(matchPlayer)

                        var matches = [];
                        var match = new Axes2Match(generateUniqueId(), "", matchPlayers);
                        matches.push(match);

                        var round = new Axes2Round(generateUniqueId(), "", "Solo", "Round " + rndData.round_number, matches);

                        this.soloData.rounds.push(round);
                        this.soloData.total = grandTotal;

                    }
                }

                this.pleaseWait = false;
                
                // Reset form
                this.fileName = null;
                this.selectedFile = null;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }

            } catch (error) {
                console.error("Upload failed:", error); 
                alert("Upload failed: " + error.message);
                this.pleaseWait = false;
            }
        },
    },
    beforeMount() {
    },
    mounted(){
    }
}

//handle page refresh with confirmation message ("Reload site?  Changes you made may not be saved.")
window.addEventListener("beforeunload", handleRefresh);



</script>

<style lang="scss" scoped>
.uploadsolo {
    background-color: #121212; /* Darker, cleaner black */
    color: #e0e0e0;
    min-height: 100vh;
    padding: 20px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    letter-spacing: normal;
}
.ai-note {
    background-color: #2a2a2a;
    border-left: 4px solid #3273dc; /* Bulma info blue */
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
    color: #b5b5b5;
    font-size: 0.95rem;
}
.ai-note strong {
    color: #3273dc;
}
.ai-note i {
    color: #3273dc;
}
.template-note {
    background-color: #2a2a2a;
    border-left: 4px solid #48c774; /* Bulma success green */
    padding: 15px;
    margin-top: 20px;
    margin-bottom: 20px;
    border-radius: 4px;
    color: #b5b5b5;
    font-size: 0.95rem;
}
.template-note i {
    color: #48c774;
}
.page-header-custom {
    color: #ffdd57; /* Axe yellow */
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 20px;
    display: inline-block;
}
.uploadsoloform {
    border: 1px solid #48c774;
    padding: 20px;
    border-radius: 8px;
    background-color: #1a1a1a;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
    position: relative;
}
.file-cta {
    background-color: #333;
    border-color: #444;
}
.file-name {
    background-color: #222;
    border-color: #444;
    color: #ccc;
}
.upload-btn {
    margin-top: 15px;
    font-weight: bold;
    transition: transform 0.1s;
}
.upload-btn:active {
    transform: scale(0.98);
}
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    z-index: 10;
}

.basicScore {
    padding: 5px; 
    margin: 2px; 
    font-size: 1.2em; 
    width: 35px; 
    border-radius: 4px;
    border: 1px solid #444;
    background-color: #333;
    color: white;
    text-align: center;
}
.totalScore {
    padding: 5px; 
    margin: 2px; 
    font-size: 1.2em; 
    width: 45px; 
    border-radius: 4px; 
    background-color: #444;
    border: 1px solid #555;
    color: white;
    text-align: center;
}
.total-wrapper {
    white-space: nowrap;
    display: inline-block;
}
.finalScore {
    padding: 5px; 
    margin: 2px; 
    font-size: 1.3em; 
    width: 80px; 
    border-radius: 4px; 
    background-color: #444;
    border: 1px solid #555;
    color: #48c774;
    font-weight: bold;
    text-align: center;
}
.submitButton {
    background-color: #48c774; 
    color: white; 
    font-size: 1.2em; 
    font-weight: bold; 
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
}
.submitButton:hover {
    background-color: #3ec46d;
}
.cancel-btn {
    margin-left: 10px;
    font-weight: bold;
}
.roundTitle {
     color: #ffdd57; /* Bulma warning yellow - previously .rountTitle typo */
     font-size: 1.2em;
     font-weight: bold;
     margin-top: 10px;
}
.finalTotal {
    font-size: 1.5em; 
    color: #ff3860;
    font-weight: bold;
}
.date-input {
    background-color: #333;
    color: white;
    border: 1px solid #444;
    max-width: 200px;
}
::-webkit-calendar-picker-indicator {
    filter: invert(1);
}
</style>